import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { apiBaseUrl } from "../../constants/urls";
import { getToken } from "../../auth/AuthToken";

const initialState = {
  sessionsList: [],
  currentSession: null,
  listLoading: false,
  sessionLoading: false,
  totalCount: 0,
};

export const getAllHemscapSessions = createAsyncThunk(
  "hemscapSessions/getAll",
  async (
    { searchQuery = "", page, rowsPerPage, dateFrom = "", dateTo = "", userId = "", status = "", exerciseKey = "" },
    { rejectWithValue }
  ) => {
    const bearerToken = getToken();
    const params = new URLSearchParams({
      q:        searchQuery,
      page:     page + 1,
      per_page: rowsPerPage,
    });
    if (dateFrom)     params.append("date_from",    dateFrom);
    if (dateTo)       params.append("date_to",      dateTo);
    if (userId)       params.append("user_id",      userId);
    if (status)       params.append("status",       status);
    if (exerciseKey)  params.append("exercise_key", exerciseKey);

    const url = `${apiBaseUrl}/admin/hemscap_sessions?${params.toString()}`;
    try {
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${bearerToken}` },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const getHemscapSession = createAsyncThunk(
  "hemscapSessions/getOne",
  async ({ sessionId }, { rejectWithValue }) => {
    const bearerToken = getToken();
    try {
      const response = await axios.get(
        `${apiBaseUrl}/admin/hemscap_sessions/${sessionId}`,
        { headers: { Authorization: `Bearer ${bearerToken}` } }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Dry-runs a draft mapping blueprint against a stored session or a pasted
// payload. The result is consumed locally by the builder (it changes on every
// edit), so it isn't kept in the slice.
export const previewHemscapMapping = createAsyncThunk(
  "hemscapSessions/previewMapping",
  async ({ sessionId, payload, mapping }, { rejectWithValue }) => {
    const bearerToken = getToken();
    try {
      const response = await axios.post(
        `${apiBaseUrl}/admin/hemscap_mapping_previews`,
        payload ? { payload, mapping } : { session_id: sessionId, mapping },
        { headers: { Authorization: `Bearer ${bearerToken}` } }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const hemscapSessionsSlice = createSlice({
  name: "hemscapSessions",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getAllHemscapSessions.pending, (state) => {
        state.listLoading = true;
      })
      .addCase(getAllHemscapSessions.fulfilled, (state, action) => {
        state.listLoading = false;
        state.sessionsList = action.payload.hemscap_sessions || [];
        state.totalCount = action.payload.meta?.total_count || 0;
      })
      .addCase(getAllHemscapSessions.rejected, (state) => {
        state.listLoading = false;
      })
      .addCase(getHemscapSession.pending, (state) => {
        state.sessionLoading = true;
        state.currentSession = null;
      })
      .addCase(getHemscapSession.fulfilled, (state, action) => {
        state.sessionLoading = false;
        state.currentSession = action.payload;
      })
      .addCase(getHemscapSession.rejected, (state) => {
        state.sessionLoading = false;
      });
  },
});

export default hemscapSessionsSlice.reducer;
